import styled from '@emotion/styled';
import React, { FC, useEffect, useRef, useMemo, memo } from 'react';
import { useCurrency } from '~Hooks';
import { Box } from '~Styles';
import { CartItem as CartItemType, LocalItem } from '~Types';
import { CombinedItem } from '~Utils/combineArticles';
import { isServiceChargeItem } from '~Utils/serviceCharge';

export const CartItemWrapper = styled.div<{ selected: boolean }>`
  padding: 6px 12px;
  color: var(--colorWhite);
  letter-spacing: 0.24px;
  font-size: 14px;
  flex: 1 1 100%;

  background: ${({ selected }) => (selected ? 'var(--colorBlueGray1000)' : 'transparent')};
`;

export const CartItemName = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  padding-right: 8px;
`;

const VariationsList = styled.div<{ isRemoved?: boolean }>`
  font-size: 12px;
  margin-top: 4px;
  color: ${({ isRemoved }) => (isRemoved ? 'var(--colorUIRed)' : 'var(--colorBlueGray500)')};
`;

const CartItemMessage = styled.div`
  color: var(--colorUISand);
`;

const CartItemCount = styled.span`
  color: var(--colorBlueGray300);
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
  opacity: 0.7;
  margin-right: 8px;
`;

interface Props {
  item: LocalItem | CombinedItem<CartItemType>;
  isSelected: boolean;
  compressedCart: boolean;
  toggleSelectedItem: (
    item: CartItemType | CombinedItem<CartItemType> | LocalItem,
    compressedCart: boolean,
  ) => void;
  hidePrice?: boolean;
}

/** Displays an item in a button in the Ordering flow for Handheld */
const CartItem: FC<Props> = ({ item, isSelected, compressedCart, toggleSelectedItem, hidePrice = false }) => {
  const currency = useCurrency();

  const countRef = useRef<HTMLSpanElement>(null);
  const count = 'count' in item ? item.count : 1;

  const variations = item.variations?.map((v) => v.name) ?? [];
  const removedVariations = item.removedVariations?.map((v) => v.name) ?? [];

  useEffect(() => {
    if (countRef.current) {
      countRef.current.style.color = isSelected ? 'var(--colorUISand)' : 'var(--colorBlueGray300)';
    }
  }, [isSelected]);

  return useMemo(
    () => (
      <CartItemWrapper
        key={item.id}
        data-testid="cart-item"
        onClick={() => toggleSelectedItem(item, compressedCart)}
        selected={isSelected}>
        <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-between">
          <CartItemName>
            <CartItemCount ref={countRef}>{count}x</CartItemCount> {item.title}
          </CartItemName>
          {!hidePrice && (
            <div>
              {item.discount && item.amount ? (
                <StrikeThrough>{currency.format(item.amount)}</StrikeThrough>
              ) : null}
              {isServiceChargeItem(item.title)
                ? '-'
                : item.amount
                ? currency.format(item.amount - (item.discount ?? 0))
                : ''}
            </div>
          )}
        </Box>
        {variations.length > 0 && <VariationsList>{variations.join(', ')}</VariationsList>}
        {removedVariations.length > 0 && (
          <VariationsList isRemoved>{removedVariations.join(', ')}</VariationsList>
        )}
        {item.message && <CartItemMessage>{item.message}</CartItemMessage>}
      </CartItemWrapper>
    ),
    [
      isSelected,
      compressedCart,
      toggleSelectedItem,
      count,
      variations,
      removedVariations,
      item.message,
      item.amount,
      item.discount,
      item.title,
      item.id,
    ],
  );
};

export default memo(CartItem);
